/**
 * @author David Roman <david@inarix.com>
 * @file Routing Pages Principal
 * @desc Created on 2023-08-17
 * @copyright Inarix
 */

import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  autoRefreshToken,
  handleVersionUpdate,
  orgFetch,
  signIn,
  TokenPayload,
} from "./redux/actions/authentication/authentication";

import {
  disableErrorLogin,
  isPortalVersionDifferent,
  setUsername,
  setPortalVersion,
} from "./redux/slices/authentication/authentication";

import { ThemeProvider } from "@mui/styles";
import { State } from "./redux/slices/index";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import themeInarix from "./components/StylesMuiInarix";

import { ThunkDispatch } from "@reduxjs/toolkit";

import PrivateRoute from "./components/authentication/PrivateRoute";
import PublicRoute from "./components/authentication/PublicRoute";

import { isTokenExpired } from "./utils/authentication";

import Map from "./components/map/Map";
import UserGroups from "./components/userGroups/UserGroups";
import UsersDevices from "./components/userDevices/UserDevices";
import UsersActions from "./components/userActions/UsersActions";
import Projects from "./components/projects/Projects";
import UsersIndividualAction from "./components/userActions/IndividualAction";
import Dashboard from "./components/dashboard/Dashboard";
import { BodyPortalVersion } from "./components/authentication/ModalVersion";
import { Modal } from "@mui/material";
import pjson from "../package.json";
import IndividualProject from "./components/projects/IndividualProject/IndividualProject";
import NotFound from "./components/NotFound";
import Login from "./components/authentication/Login";
import RecoverPassword from "./components/authentication/ResetPassword";
import UserLocations from "./components/userLocations/locations";
import { usePostHog } from "posthog-js/react";
import DataSampleView from "./components/data/dataSampleView/DataSampleView";
import SetNewPassword from "./components/authentication/SetNewPassword";
import Activate from "./components/authentication/ActivateAccount";
import CreateLocation from "./components/userLocations/CreateLocation";
import StepConfigCreateUsers from "./components/userLocations/steps/stepConfigUsers/UsersConfigTable";

import CreateUsers from "./components/userGroups/creationUsers/MainCreateUsers";
import StepConfigCreateUsersFromUsers from "./components/userGroups/creationUsers/stepsSiteUsers/Step2-2";
import StepConfigCreateManagerUsers from "./components/userGroups/creationUsers/managerUsers/managerUsersPartsOfTable/UsersManagersConfigTable";
import jwt from "jsonwebtoken";
import { listActionsScenariosGeneral } from "./redux/actions/masterInfoActions/ActionsScenarios";
import { fetchLocations } from "./redux/actions/locations/locationsAsyncActions";

import { logOut, selectLocale } from "./redux/slices/layout";
import { LicencesPage } from "./components/licenses/LicensePage";
import LocationDetails from "./components/userLocations/locationDetailsGrainTracker/locationDetails";
import EditLocation from "./components/userLocations/editLocation/EditLocation";
import LinkUsersToLocations from "./components/userLocations/editLocation/LinkUsersToLocation";
import EditGrainTrackerBins from "./components/grainWise/edit/EditGrainTrackerBins";

import ShareSite from "./components/userLocations/shareLocationGrainTracker/ShareSite";
import { initializeFaro, getWebInstrumentations } from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { UserActionInstrumentation } from "@grafana/faro-web-sdk";

import localforage from "localforage";
// logout

import { IdleTimerProvider } from "react-idle-timer";
import DataT from "./components/dataTable/Data";

const rememberme = localStorage.getItem("rememberme");

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { user } = useSelector((state: State) => state.Authentication);

  return {
    signOutUser: () => {
      // Only dispatch logOut if user exists to avoid unnecessary REVERT_ALL
      if (user !== null) {
        dispatch(logOut());
      }

      localStorage.removeItem("access_token");

      if (rememberme === "false") {
        localStorage.removeItem("password");
        localStorage.removeItem("username");
      }
      setTimeout(() => {
        localforage.removeItem("persist:root");
        localStorage.removeItem("persist:root");
      }, 1000);
    },

    signIn: () => {
      dispatch(signIn());
    },

    handleVersionUpdate: () => {
      dispatch(handleVersionUpdate(null));
    },

    startRefreshingToken: () => dispatch(autoRefreshToken()),
    setUserName: (value: string) => dispatch(setUsername(value)),
    disableError: () => dispatch(disableErrorLogin()),
    fetchOrg: (realmId: number, token: string, locale: string) =>
      dispatch(orgFetch({ realmId, token, locale })),
    isPortalVersionDifferent: (value: boolean) =>
      dispatch(isPortalVersionDifferent(value)),
    setPortalVersion: (version: string) => dispatch(setPortalVersion(version)),
    prefetchGeneralInformation: (token: string, orgId: number) => {
      dispatch(listActionsScenariosGeneral({ token, orgId }));
    },
    initLocations: (token: string) => dispatch(fetchLocations(token)),
  };
};
export const blah = () => void 0;

const App: FC = () => {
  initializeFaro({
    url: process.env.REACT_APP_FARO_URL,
    app: {
      name: "inarix-portal",
      version: pjson.version,
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new UserActionInstrumentation(),
      new TracingInstrumentation(),
    ],
  });

  const posthog = usePostHog();
  const locale = useSelector(selectLocale);
  const {
    authenticated,
    user,
    isThePortalVersionDifferent,
    portalVersion,
    noAccess,
    username,
    error,
    errorAuthenticated,
    errorRefreshToken,
  } = useSelector((state: State) => state.Authentication);

  const minutes = 1000 * 60;

  const token = user?.token ?? "";

  const {
    startRefreshingToken,
    signOutUser,
    isPortalVersionDifferent,

    disableError,
    fetchOrg,
    prefetchGeneralInformation,
    initLocations,
    handleVersionUpdate,
  } = SetActions();

  const managingPrefetch = (realmId: number) => {
    prefetchGeneralInformation(token, realmId);
  };

  const onIdle = () => {
    signOutUser();
    setTimeout(() => {
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    }, 1000);
  };
  const PortalVersionJSON = pjson.version;

  const isThePortalVersionCorrect = () => {
    if (portalVersion.version !== PortalVersionJSON) {
      isPortalVersionDifferent(true);
    }
  };

  if (noAccess === 403) {
    signOutUser();
  }

  if (authenticated) {
    posthog.identify(username);
  }

  if (error || errorAuthenticated) {
    setTimeout(() => {
      disableError();
    }, 5000);
  }

  if (errorRefreshToken) {
    isTokenExpired(user?.token) === true
      ? signOutUser()
      : startRefreshingToken();
  }

  window.addEventListener("storage", (e) => {
    if (e.key === "access_token" && e.oldValue && !e.newValue) {
      signOutUser();
    }
  });

  window.addEventListener("storage", (event) => {
    if (event.storageArea !== localStorage) return;
    if (event.key === "access_token") {
      setTimeout(() => {
        // eslint-disable-next-line no-restricted-globals
        location.reload();
      }, 1500);
    }
  });

  useEffect(() => {
    if (authenticated) {
      const { realmId }: TokenPayload = jwt.decode(token) as TokenPayload;

      fetchOrg(realmId, token, locale);
      initLocations(token);
      setInterval(() => {
        if (!isTokenExpired(user?.token)) {
          return startRefreshingToken();
        } else {
          return () => {};
        }
      }, minutes * 7);
      if (isTokenExpired(user?.token)) {
        signOutUser();
      }

      managingPrefetch(realmId);
      startRefreshingToken();
      isTokenExpired(user?.token) === true ? signOutUser() : blah();
    }

    isThePortalVersionCorrect();
    localStorage.setItem("access_token", user?.token as string);
  }, [authenticated]);

  useEffect(() => {
    const intervalIdle = setInterval(() => {
      isTokenExpired(user?.token) ? signOutUser() : console.log("");
    }, minutes * 0.5);
    return () => {
      clearInterval(intervalIdle);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IdleTimerProvider timeout={minutes * 12} onIdle={onIdle}>
      <ThemeProvider theme={themeInarix(locale)}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/activate/:id"
              element={
                <PublicRoute user={authenticated}>
                  <Activate />
                </PublicRoute>
              }
            />

            <Route
              path="/login"
              element={
                <PublicRoute user={authenticated}>
                  <Login />
                </PublicRoute>
              }
            />

            <Route
              path="/password-change/:id"
              element={
                <PublicRoute user={authenticated}>
                  <SetNewPassword />
                </PublicRoute>
              }
            />

            <Route
              path="/recoverPassword"
              element={
                <PublicRoute user={authenticated}>
                  <RecoverPassword />
                </PublicRoute>
              }
            />

            <Route
              path="/"
              element={
                <PrivateRoute user={authenticated}>
                  <Map />
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute user={authenticated}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute user={authenticated}>
                  <UserGroups />
                </PrivateRoute>
              }
            />

            <Route
              path="/devices"
              element={
                <PrivateRoute user={authenticated}>
                  <UsersDevices />
                </PrivateRoute>
              }
            />

            <Route
              path="/locations"
              element={
                <PrivateRoute user={authenticated}>
                  <UserLocations />
                </PrivateRoute>
              }
            />

            <Route
              path="/site/create"
              element={
                <PrivateRoute user={authenticated}>
                  <CreateLocation />
                </PrivateRoute>
              }
            />
            <Route
              path="/site/create?id=:id"
              element={
                <PrivateRoute user={authenticated}>
                  <CreateLocation />
                </PrivateRoute>
              }
            />

            <Route
              path="/site/edit/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <EditLocation />
                </PrivateRoute>
              }
            />

            <Route
              path="/site/edit/storage/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <EditGrainTrackerBins />
                </PrivateRoute>
              }
            />

            <Route
              path="/site/share/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <ShareSite />
                </PrivateRoute>
              }
            />

            <Route
              path="/site/link-users/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <LinkUsersToLocations />
                </PrivateRoute>
              }
            />

            <Route
              path="/site/details/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <LocationDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-locations/configs"
              element={
                <PrivateRoute user={authenticated}>
                  <StepConfigCreateUsers />
                </PrivateRoute>
              }
            />

            <Route
              path="/create-users"
              element={
                <PrivateRoute user={authenticated}>
                  <CreateUsers />
                </PrivateRoute>
              }
            />

            <Route
              path="/create-users/configs"
              element={
                <PrivateRoute user={authenticated}>
                  <StepConfigCreateUsersFromUsers />
                </PrivateRoute>
              }
            />

            <Route
              path="/create-users/configs-managers"
              element={
                <PrivateRoute user={authenticated}>
                  <StepConfigCreateManagerUsers />
                </PrivateRoute>
              }
            />

            <Route
              path="/actions/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <UsersIndividualAction />
                </PrivateRoute>
              }
            />

            <Route
              path="/actions"
              element={
                <PrivateRoute user={authenticated}>
                  <UsersActions />
                </PrivateRoute>
              }
            />

            <Route
              path="/projects"
              element={
                <PrivateRoute user={authenticated}>
                  <Projects />
                </PrivateRoute>
              }
            />

            <Route
              path="/projects/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <IndividualProject />
                </PrivateRoute>
              }
            />

            <Route
              path="/data"
              element={
                <PrivateRoute user={authenticated}>
                  <DataT />
                </PrivateRoute>
              }
            />
            <Route
              path="/data/:id"
              element={
                <PrivateRoute user={authenticated}>
                  <DataSampleView />
                </PrivateRoute>
              }
            />
            <Route
              path="/licenses"
              element={
                <PrivateRoute user={authenticated}>
                  <LicencesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="*"
              element={
                <PublicRoute user={authenticated}>
                  <NotFound />
                </PublicRoute>
              }
            />
            <Route
              path="*"
              element={
                <PrivateRoute user={authenticated}>
                  <NotFound />
                </PrivateRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Modal open={isThePortalVersionDifferent}>
          <BodyPortalVersion onUpdate={handleVersionUpdate} />
        </Modal>
      </ThemeProvider>
    </IdleTimerProvider>
  );
};

export default App;
